import { useMutation, useQuery } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloClient } from '@apollo/client/core';
import {
  BackgroundIcon,
  getHeaderValue,
  getTranslatedText,
  Icon,
  Loader,
  setFaviconAndAppTitle
} from '@nile/components';
import envConfig from '@nile/home/src/Config/env';
import hashHistory from '@nile/home/src/hashHistory';
import { Button } from '@superops/ui-design-system';
import { ThumbsDown } from '@styled-icons/fa-solid/ThumbsDown';
import { ThumbsUp } from '@styled-icons/fa-solid/ThumbsUp';
import { createHttpLink } from 'apollo-link-http';
import dompurify from 'dompurify';
import gql from 'graphql-tag';
import React, { useEffect, useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import Rating from 'react-rating-stars-component';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { dompurifier } from '@nile/components/src/Utils/helpers';

const iconStyle = {
  width: 18,
  height: 18,
  marginRight: '15px',
  cursor: 'pointer'
};

const GET_SURVEY = gql`
  query getFeedbackSurvey($surveyId: ID!) {
    getFeedbackSurvey(surveyId: $surveyId) {
      surveyId
      type
      status
      question
      enableCommments
    }
  }
`;

const FEEDBACK_SUBMIT = gql`
  mutation(
    $surveyId: ID!
    $ticketId: ID!
    $input: FeedbackSurveyResponseInput
  ) {
    respondFeedbackSurvey(
      surveyId: $surveyId
      ticketId: $ticketId
      input: $input
    ) {
      points
      comments
    }
  }
`;

const cache = new InMemoryCache({
  addTypename: false
});

const CSATSurvey = (props) => {
  const { intl } = props;
  const params = new URLSearchParams(props.location.search);
  const token = params.get('token');
  const point = params.get('point');
  const surveyId = params.get('surveyId');
  const ticketId = params.get('ticketId');
  const publicClient = new ApolloClient({
    cache,
    link: createHttpLink({
      uri: localStorage?.getItem('apiDomain') + envConfig.ticketGateway,
      credentials: 'include',
      headers: {
        ...getHeaderValue(),
        authorization: `Bearer nl-${token}`
      }
    }),
    connectToDevTools: true
  });
  return (
    <CSATPage
      point={point}
      surveyId={surveyId}
      ticketId={ticketId}
      publicClient={publicClient}
      intl={intl}
    />
  );
};

const CSATPage = (props) => {
  const [showFeedbackSuccess, setShowFeedbackSuccess] = useState(false);
  const { publicClient, ticketId, surveyId, point, intl } = props;
  const [points, setPoints] = useState(point);
  const [comments, setComments] = useState('');
  const [btnLoading, setLoading] = useState(false);

  const { data: surveyData, loading, error } = useQuery(GET_SURVEY, {
    client: publicClient,
    variables: {
      surveyId
    }
  });

  const [submitSurvey] = useMutation(FEEDBACK_SUBMIT, {
    client: publicClient,
    onCompleted({ result }) {
      setShowFeedbackSuccess(true);
      setLoading(false);
    },
    onError() {
      toast('Something went wrong');
    }
  });

  const submitForm = () => {
    setLoading(true);
    submitSurvey({
      variables: {
        surveyId,
        ticketId,
        input: { points, comments: dompurifier(comments) }
      }
    });
  };

  if (error) {
    hashHistory.push('/error/unauthorizedAccess', { errors: error });
  }

  useEffect(() => {
    setFaviconAndAppTitle();
  }, []);

  return (
    <div className="flex jctr fh import-card-background">
      {loading ? (
        <Loader />
      ) : showFeedbackSuccess ? (
        <SuccessFeedbackContainer className="flex col center jctr tcenter mfh ">
          <BackgroundIcon
            width="300px"
            height="300px"
            url={`url(${envConfig.imageRoot}svg/success-feedback.svg)`}
          />
          <p className="success-description ">
            {getTranslatedText(intl, 'admin.customer.survey.success.message')}
          </p>
        </SuccessFeedbackContainer>
      ) : (
        <ContentCard>
          <p className="title">
            {getTranslatedText(intl, 'admin.customer.survey')}
          </p>
          <p className="question">{surveyData?.getFeedbackSurvey?.question}</p>
          <div className="mt20 flex">
            {surveyData?.getFeedbackSurvey?.type === 'THUMBSUP' ? (
              <div className="flex">
                <FeedbackButton
                  selected={Number(points) === 1}
                  onClick={() => setPoints(1)}
                >
                  <SVGIcon name="ThumbsUp" color="#74B62F" />
                  <span>
                    {getTranslatedText(intl, 'admin.customer.survey.satisfied')}
                  </span>
                </FeedbackButton>
                <FeedbackButton
                  selected={Number(points) === 0}
                  onClick={() => setPoints(0)}
                >
                  <SVGIcon name="ThumbsDown" color="#FA6A6A" />
                  <span>
                    {getTranslatedText(
                      intl,
                      'admin.customer.survey.not.satisfied'
                    )}
                  </span>
                </FeedbackButton>
              </div>
            ) : points ? (
              <Rating
                onChange={(data) => setPoints(data)}
                size={50}
                count={5}
                value={Number(points)}
                classNames="lh-unset"
              />
            ) : (
              ''
            )}
          </div>
          {surveyData?.getFeedbackSurvey?.enableCommments && (
            <CommentsSection>
              <p className="add-comments">
                {getTranslatedText(intl, 'admin.customerSatisfaction.comments')}
              </p>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="comments-section"
              />
            </CommentsSection>
          )}
          <div className="mt20">
            <Button
              id="csatButtonSavePage"
              loading={btnLoading}
              onClick={submitForm}
              primary
            >
              <FormattedMessage id="common.submit" />
            </Button>
          </div>
        </ContentCard>
      )}
    </div>
  );
};

const SuccessFeedbackContainer = styled.div`
  .success-description {
    font-size: 40px;
    color: #170426;
    font-weight: 800;
    width: 400px;
    width: 65%;
    line-height: 47px;
  }
`;

const CommentsSection = styled.div`
  margin-top: 40px;
  margin-bottom: 20px;
  .add-comments {
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 10px;
  }
  .comments-section {
    width: 100%;
    height: 100px;
    resize: none;
  }
`;

const FeedbackButton = styled.div`
  box-sizing: border-box;
  border-radius: 5px;
  margin-right: 10px;
  padding: 8px 15px;
  cursor: pointer;
  border: ${(props) =>
    `${props.selected ? '1px solid #000000' : '1px solid #9C9C9C'}`};
  opacity: ${(props) => `${props.selected ? '1' : '0.5'}`};
`;

const ContentCard = styled.div`
  background: #ffffff;
  border: 1px solid #e9e9e9;
  box-sizing: border-box;
  box-shadow: 0px 2px 4px rgba (0, 0, 0, 0.102874);
  border-radius: 10px;
  width: 789px;
  height: max-content;
  overflow: auto;
  margin-top: 50px;
  padding: 30px;
  .title {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.8);
  }
  .question {
    color: rgba(0, 0, 0, 0.8);
    font-size: 20px;
    font-weight: bold;
    margin-top: 30px;
  }
  .Superops-Button {
    float: right;
    background-color: #0b0114;
  }
`;
export default injectIntl(withRouter(CSATSurvey));
